import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, X } from 'lucide-react';
import { useTransferPayment } from '../../../api/hooks/useBilling';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/actions/Button';
import { Select } from '../../../components/inputs/Select';
import { Alert } from '../../../components/feedback/Alert';
import type { BillingInvite } from '../../../types';

interface Props {
  source: BillingInvite;
  allInvites: BillingInvite[];
  onClose: () => void;
}

export function TransferPaymentModal({ source, allInvites, onClose }: Props) {
  const [targetId, setTargetId] = useState<number | null>(null);
  const transfer = useTransferPayment();
  const { showToast } = useToast();

  const candidates = allInvites.filter(i => i.id !== source.id && !i.paid_at);

  const handleTransfer = async () => {
    if (!targetId) return;
    try {
      await transfer.mutateAsync({ sourceId: source.id, targetId });
      showToast('Payment transferred successfully.', 'success');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showToast(msg || 'Transfer failed. Please try again.', 'error');
    }
  };

  const targetOptions = [
    { value: '', label: 'Select an invite…' },
    ...candidates.map(i => ({ value: String(i.id), label: i.title })),
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background-card rounded-xl shadow-xl z-50 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Transfer Payment</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-background flex items-center justify-center text-text-secondary"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Current invite */}
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Current invite</p>
            <div className="bg-background rounded-lg border border-border px-4 py-3">
              <p className="text-sm font-medium text-text-primary">{source.title}</p>
              <p className="text-xs text-text-secondary mt-0.5">
                Paid {new Date(source.paid_at!).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowDown size={16} className="text-text-secondary" />
          </div>

          {/* Transfer invite */}
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Transfer invite</p>
            {candidates.length === 0 ? (
              <div className="bg-background rounded-lg border border-border px-4 py-3 text-sm text-text-secondary">
                No eligible invites — all others are already paid.
              </div>
            ) : (
              <Select
                value={targetId ? String(targetId) : ''}
                onChange={(val) => setTargetId(val ? Number(val) : null)}
                options={targetOptions}
              />
            )}
          </div>

          {/* Warning */}
          <Alert variant="warning">
            <ul className="list-disc list-inside space-y-0.5">
              <li><span className="font-medium">{source.title}</span> will be unpublished</li>
              <li>Payment moves to the selected invite</li>
              <li>Once a guest RSVPs or you email this invite, payment cannot be transferred</li>
            </ul>
          </Alert>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-border bg-background">
          <Button variant="secondary" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!targetId || transfer.isPending}
            fullWidth
          >
            {transfer.isPending ? 'Transferring…' : 'Transfer'}
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
